import { useState } from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '@/utils/cn';
import { MasterSearchModal } from '@/components/master/MasterSearchModal';
import type { SearchColumn } from '@/components/master/MasterSearchModal';
import { Badge } from '@/components/ui/Badge';
import { AccountLedgerButton, ledgerShortcut } from '@/components/erp/AccountLedgerButton';
import { accountApi } from '@/api/account.api';
import type { AccountListDto, AccountType } from '@/types/account.types';

const TYPE_BADGE: Record<AccountType, string> = {
  Asset:     'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  Liability: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  Equity:    'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  Revenue:   'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  Expense:   'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
};

const SEARCH_COLUMNS: SearchColumn<AccountListDto>[] = [
  { key: 'code',  label: 'Code',  className: 'w-24 font-mono' },
  { key: 'name',  label: 'Name' },
  { key: 'accountType', label: 'Type', className: 'w-24',
    render: r => (
      <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-semibold', TYPE_BADGE[r.accountType])}>
        {r.accountType}
      </span>
    ) },
  { key: 'isPostingAccount', label: 'Mode', className: 'w-20 text-center',
    render: r => (
      <Badge variant={r.isPostingAccount ? 'default' : 'success'}>
        {r.isPostingAccount ? 'Posting' : 'Header'}
      </Badge>
    ) },
];

interface AccountLookupFieldProps {
  label?:           string;
  value?:           string | null;   // account id (Guid)
  displayCode?:     string | null;   // account code
  displayName?:     string | null;   // account name
  onChange:         (id: string | null, code: string | null, name: string | null) => void;
  disabled?:        boolean;
  excludeId?:       string;          // exclude the current account itself
  hint?:            string;
  /** Filter by posting type. true = posting accounts only, false = header/group only, undefined = all accounts */
  isPostingAccount?: boolean;
  placeholder?:     string;
}

export const AccountLookupField = ({
  label = 'Account',
  value,
  displayCode,
  displayName,
  onChange,
  disabled = false,
  hint,
  isPostingAccount,
  placeholder,
}: AccountLookupFieldProps) => {
  const [searchOpen, setSearchOpen] = useState(false);

  const fetchAccounts = async (search: string, page: number, pageSize: number) => {
    const res = await accountApi.getList({
      search, pageNumber: page, pageSize,
      ...(isPostingAccount !== undefined ? { isPostingAccount } : {}),
    });
    const d = res.data.data;
    return { items: d?.items ?? [], totalCount: d?.totalCount ?? 0 };
  };

  const handleSelect = (item: AccountListDto) => {
    setSearchOpen(false);
    onChange(item.id, item.code, item.name);
  };

  const handleClear = () => onChange(null, null, null);

  const hasValue = !!value;

  return (
    <div onKeyDownCapture={ledgerShortcut(value)}>
      {label && (
        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
          {label}
        </label>
      )}

      {/* Focusable so F9 has somewhere to land: click or Tab to the box, then F9. */}
      <div tabIndex={hasValue ? 0 : -1} className={cn(
        'flex items-center h-9 rounded-md border text-sm outline-none focus:ring-2 focus:ring-brand-500/40',
        'bg-white dark:bg-gray-800',
        disabled
          ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50'
          : 'border-gray-300 dark:border-gray-600',
      )}>
        {/* Value display */}
        <div className="flex-1 flex items-center gap-2 px-3 min-w-0">
          {hasValue ? (
            <>
              <span className="font-mono text-xs text-brand-600 dark:text-brand-400 flex-shrink-0">
                {displayCode}
              </span>
              <span className="text-gray-500 flex-shrink-0">—</span>
              <span className="text-gray-700 dark:text-gray-300 truncate">{displayName}</span>
            </>
          ) : (
            <span className="text-gray-400 italic text-xs">
              {placeholder ?? 'None selected'}
            </span>
          )}
        </div>

        {/* Ledger listing (422175) — also in view mode */}
        <AccountLedgerButton accountId={value} className="w-7 h-7 justify-center" />

        {/* Clear button */}
        {hasValue && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="flex-shrink-0 w-7 h-7 flex items-center justify-center text-gray-400 hover:text-rose-500 transition-colors"
            title="Clear parent"
          >
            <X size={12} />
          </button>
        )}

        {/* Browse button */}
        {!disabled && (
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            className={cn(
              'flex-shrink-0 w-8 h-full flex items-center justify-center',
              'border-l border-gray-200 dark:border-gray-600 rounded-r-md',
              'text-gray-400 hover:text-brand-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors',
            )}
            title={`Browse ${label}`}
          >
            <Search size={13} />
          </button>
        )}
      </div>

      {hint && (
        <p className="mt-0.5 text-[10px] text-gray-400">{hint}</p>
      )}

      <MasterSearchModal<AccountListDto>
        isOpen={searchOpen}
        onClose={() => setSearchOpen(false)}
        title={`Select ${label}`}
        columns={SEARCH_COLUMNS}
        fetchData={fetchAccounts}
        onSelect={handleSelect}
      />
    </div>
  );
};
